import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const PassortList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            console.log("Fetching passport entries...");
            const response = await axios.post('https://chaman.gob.pk/eac_backend/passcert.php', {
                action: 'get_records',
            });

            console.log("Response received:", response.data);

            // Check if response has data property (new structure)
            if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                const sortedEntries = response.data.data
                    .map(entry => {
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp);

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else if (Array.isArray(response.data)) {
                // Handle old structure (direct array)
                console.log("Using old response structure (direct array)");
                const now = new Date();
                setLastRefreshTime(now);

                const sortedEntries = response.data
                    .map(entry => {
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp);

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Unexpected response format:', response.data);
                setError('Unexpected data format received from server');
            }
        } catch (err) {
            console.error('Error fetching entries:', err);
            setError('Failed to fetch Passport entries: ' + (err.message || 'Network error'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/passcert.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-passport', { state: { entry } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handlePrint = async (entry) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
    
        // Add logo image and text
        doc.setFontSize(10);
        const imageWidth = 0.8 * 28.5;
        const imageHeight = 0.6 * 28.8;
        const xPosition = 14;
        const yPosition = 16;
        doc.addImage(logoImage, 'PNG', xPosition, yPosition, imageWidth, imageHeight);
    
        // Format dates
        const formattedDate = formatDate(entry.date);
        const formattedDate2 = formatDate(entry.date_2);
        
        // Right align X coordinate
        const rightAlignX = 135;
        const maxTextWidth = 60;
    
        // No DC line
        doc.setFontSize(11);
        doc.setFont("normal");
        const noDCText = `No.Veri/Passport/DC/CMN ${entry.local_no || ''}`;
        doc.text(noDCText, rightAlignX + maxTextWidth / 2 - doc.getTextWidth(noDCText) / 2, 18);
    
        // OFFICE OF THE line
        doc.setFontSize(11);
        doc.setFont("normal");
        const officeText = "OFFICE OF THE";
        doc.text(officeText, rightAlignX + maxTextWidth / 2 - doc.getTextWidth(officeText) / 2, 23);
    
        // DEPUTY COMMISSIONER CHAMAN line
        doc.setFontSize(11);
        doc.setFont("normal");
        const dcText = "DEPUTY COMMISSIONER CHAMAN";
        doc.text(dcText, rightAlignX + maxTextWidth / 2 - doc.getTextWidth(dcText) / 2, 28);
    
        // Dated line
        doc.setFontSize(11);
        doc.setFont("normal");
        const datedText = `Dated the ${formattedDate}`;
        doc.text(datedText, rightAlignX + maxTextWidth / 2 - doc.getTextWidth(datedText) / 2, 33);
    
        // Additional Text
        doc.setFontSize(12);
        doc.setFont("Bold");
        doc.text('To,', 14, 47);
        doc.text('The Office Incharge Passport,', 43.5, 55);
        doc.text(`Regional Office ${entry.regional_office || ''}.`, 43.5, 60);
    
        doc.setFontSize(13);
        doc.text('Subject:', 14, 70);
        doc.setFont('times', 'bold');
        doc.text('VERIFICATION OF NATIONAL STATUS', 43.5, 70);
        doc.setFont('times', 'normal');
        doc.line(43.5, 71, 132, 71);
    
        // Reference text
        doc.setFontSize(11);
        doc.setFont('times', "normal");
        doc.text(`Reference to the letter No. ${entry.letter_no || ''} Dated ${formattedDate2} received from the`, 43.5, 78);
        doc.text('Regional Passport Office Chaman on the subject noted above.', 14, 83);
        doc.text(`Report in respect of Mr. ${entry.name || ''} S/D/W of ${entry.father_name || ''} ${entry.cnic || ''} resident of`, 43.5, 95);
        doc.text(`${entry.address || ''} duly verified by Tehsildar Chaman is enclosed`, 14, 100);
        doc.text('herewith as desired.', 14, 105);
        doc.text('The particulars of the applicant are given as below', 14, 115);
    
        // Table data
        const tableData = [
            ["Sr#", "Name", "S/D/W", "CNIC", "National Verfication", "Enclosure of Tehsildar Report", "Enclosure of Levies thana Incharge"],
            ["1", entry.name || '', entry.father_name || '', entry.cnic || '', entry.n_verification || '', entry.t_report || '', entry.i_report || ""],
        ];
    
        doc.autoTable({
            head: [tableData[0]],
            body: tableData.slice(1),
            startY: 125,
            theme: 'grid',
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center',
            },
            styles: {
                fontSize: 10,
                fontStyle: 'normal',
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
            },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 35, halign: 'left' },
                2: { cellWidth: 35, halign: 'left' },
                3: { cellWidth: 30, halign: 'center' },
                4: { cellWidth: 22, halign: 'center' },
                5: { cellWidth: 25, halign: 'center' },
                6: { cellWidth: 25, halign: 'center' },
            },
        });
    
        doc.setFontSize(11);
        doc.text("Report is hereby submitted for further process as per rules", pageWidth / 2, pageHeight - 140, { align: "center" });
    
        doc.setFontSize(11);
        doc.text("DEPUTY COMMISSIONER", 147, pageHeight - 110);
        doc.text("CHAMAN", 163, pageHeight - 105);
    
        // QR Code Generation
        const qrData = `National Verification of ${entry.name || ''} has been verified and issued by the Office of the Deputy Commissioner, Chaman.`;
        const qrCanvas = document.createElement('canvas');

        await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
        const qrDataURL = qrCanvas.toDataURL('image/png');

        const qrX = 100;
        const qrY = pageHeight - 50;

        doc.addImage(qrDataURL, 'PNG', qrX, qrY, 20, 20);

        window.open(doc.output('bloburl'));
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">{entry.token_id || 'N/A'}</div>
                <div className="table-cell">{formatDate(entry.date)}</div>
                <div className="table-cell">{entry.name || 'N/A'}</div>
                <div className="table-cell">{entry.father_name || 'N/A'}</div>
                <div className="table-cell">{entry.cnic || 'N/A'}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading Passport Records...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <div className="error-card">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <div className="error-actions">
                    <button onClick={refreshData} className="retry-btn">
                        <FontAwesomeIcon icon={faSyncAlt} /> Retry
                    </button>
                    <button onClick={() => window.location.reload()} className="reload-btn">
                        Reload Page
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2>Passport Certificate Records</h2>
                    <div className="stats-container">
                        <span className="entries-count">Total: {gdsrEntries.length}</span>
                        {newEntriesCount > 0 && (
                            <span className="new-entries-count">New: {newEntriesCount}</span>
                        )}
                        {lastRefreshTime && (
                            <span className="refresh-time">
                                Last updated: {lastRefreshTime.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            title="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by Token ID, Name, CNIC..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">F/H Name</div>
                    <div className="header-cell">CNIC</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            {searchTerm ? (
                                <>
                                    <FontAwesomeIcon icon={faSearch} size="3x" />
                                    <p>No records found matching "{searchTerm}"</p>
                                    <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faInfoCircle} size="3x" />
                                    <p>No passport records available</p>
                                    <p className="empty-subtext">Add new records using the Passport form</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    <div className="footer-info">
                        Showing {filteredEntries.length} of {gdsrEntries.length} entries
                        {searchTerm && ` (filtered)`}
                    </div>
                    <div className="footer-actions">
                        <button 
                            onClick={() => {
                                const ws = XLSX.utils.json_to_sheet(filteredEntries);
                                const wb = XLSX.utils.book_new();
                                XLSX.utils.book_append_sheet(wb, ws, "Passport Records");
                                XLSX.writeFile(wb, `passport_records_${new Date().toISOString().split('T')[0]}.xlsx`);
                            }}
                            className="export-btn"
                        >
                            <FontAwesomeIcon icon={faFileExcel} /> Export to Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PassortList;